import { KEYS } from 'game/constants/SceneConstants';
import { SceneBase } from '@jollywise/jollygoodgame';
import GameSceneBase from './GameSceneBase';
import { OnboardingPointer } from '../../display/ui/OnboardingPointer';

const AVAILABLEITEMS = [
  {
    id: 'bird_table',
    spriteKey: 'tile_bird_table',
    quality: 'Neutral',
  },
  {
    id: 'clover',
    spriteKey: 'tile_clover',
    quality: 'Good',
  },
  {
    id: 'ghetto_blaster',
    spriteKey: 'tile_ghetto_blaster',
    quality: 'Neutral',
  },
  {
    id: 'hot_dog_stand',
    spriteKey: 'tile_hot_dog_stand',
    quality: 'Bad',
  },
  {
    id: 'leafy_plants',
    spriteKey: 'tile_leafy_plants',
    quality: 'Neutral',
  },
  {
    id: 'old_bike_frame',
    spriteKey: 'tile_old_bike_frame',
    quality: 'Bad',
  },
  {
    id: 'purple_flowers',
    spriteKey: 'tile_purple_flowers',
    quality: 'Good',
  },
  {
    id: 'shopping_trolley',
    spriteKey: 'tile_shopping_trolley',
    quality: 'Bad',
  },
  {
    id: 'yellow_flowers',
    spriteKey: 'tile_yellow_flowers',
    quality: 'Good',
  },
];

export default class BeeKind extends GameSceneBase {
  constructor() {
    super({ key: KEYS.BeeKind, active: false });
  }

  create() {
    super.create();

    this.spineBG = this.add.spine(840, 360, 'bee_kind_background', '', true);
    this.spineBin = this.add.spine(840, 360, 'bee_kind_bin', '', true);
    this.spineBox = this.add.spine(840, 360, 'bee_kind_box', '', true);

    this.availableItems = Phaser.Utils.Array.Shuffle([...AVAILABLEITEMS]);

    this.parkItems = [];
    this.input.on('drag', this.handleDragMove, this);
    this.input.on('drop', this.handleDrop, this);
    this.input.on('dragend', this.handleDragEnd, this);

    this.onboardingPointer = new OnboardingPointer(this, {
      dragPositions: {
        from: [
          {
            x: 460,
            y: 390,
          },
        ],
        to: [
          { x: 840, y: 500 },
          { x: 1200, y: 500 },
        ],
      },
    }).setDepth(1);

    this.setupGame();
  }

  setupGame() {
    // some silly phaser bug I am trying to work out makes zone block buttons over the top
    // we need to create, then destroy these instead
    this.parkZones = [this.add.zone(550, 410, 550, 180).setOrigin(0).setDropZone()];
    this.binZone = this.add.zone(1110, 390, 125, 210).setOrigin(0).setDropZone();

    this.spineBG.on('event', this.handleSpineEvent, this);

    this.spineBG.once(
      'complete',
      () => {
        this.spineBox.once('complete', this.showInstruction, this);
        this.spineBin.play('in', false);
        this.spineBox.play('in', false);
      },
      this
    );
    this.spineBG.play('intro', false);
  }

  showInstruction() {
    super.showInstruction(this.game.copy.get('BeeKind_instruction'), this.startGame, this);
  }

  startGame() {
    this.spawnNextItem();
    this.onboardingPointer.enable();
  }

  handleSpineEvent(t, e) {
    if (e.data?.name === 'sfx') {
      switch (e.stringValue) {
        case 'page_open':
          this.game.soundController.playSFX('general', 'general_page_open');
          break;
        case 'page_close':
          this.game.soundController.playSFX('general', 'general_page_close');
          break;
        case 'page_popup':
          this.game.soundController.playSFX('general', 'general_popup');
          break;
      }
    }
  }

  restartGame() {
    this.parkItems?.forEach((i) => i.destroy());
    this.parkItems = [];
    this.availableItems = [...AVAILABLEITEMS].sort(() => Math.random() - 0.5); // shuffle
    this.setupGame();
  }

  spawnNextItem() {
    const randomItemIndex = Math.floor(Math.random() * this.availableItems.length);
    const randomItem = this.availableItems[randomItemIndex];
    const filtered = this.availableItems.splice(randomItemIndex, 1);

    const image = this.add
      .image(460, 450, 'bee_kind', filtered[0].spriteKey)
      .setInteractive({ useHandCursor: true });
    image.quality = randomItem.quality;
    this.tweens.add({
      targets: image,
      y: 390,
      duration: 250,
      ease: 'Quad.easeOut',
    });

    this.input.setDraggable(image);
  }

  handleDragMove(pointer, gameObject, dragX, dragY) {
    gameObject.x = dragX;
    gameObject.y = dragY;
  }

  handleDrop(pointer, gameObject, dropZone) {
    gameObject.input.enabled = false;

    if (this.parkZones.includes(dropZone)) {
      this.parkItems.push(gameObject);

      // sort park items for depth
      this.parkItems.sort((a, b) => a.y + a.height * 0.5 - (b.y + b.height * 0.5));
      this.parkItems.forEach((i) => this.children.bringToTop(i));
      this.game.soundController.playSFX('beekind', 'bee_ground');
    }

    if (dropZone === this.binZone) {
      this.game.soundController.playSFX('beekind', 'bee_recycling');
      gameObject.destroy();
    }

    gameObject.y = Math.min(gameObject.y, 600 - gameObject.height * 0.5);
    this.tweens.add({
      targets: gameObject,
      scale: 0.9,
      yoyo: true,
      duration: 125,
      ease: 'Bounce.easeOut',
    });

    if (this.availableItems.length) {
      this.spawnNextItem();
    } else {
      //this.time.delayedCall(500, this.finishGame, null, this);
      this.finishGame();
    }
  }

  handleDragEnd(pointer, gameObject, dropped) {
    if (!dropped) {
      gameObject.x = gameObject.input.dragStartX;
      gameObject.y = gameObject.input.dragStartY;
    }
  }

  finishGame() {
    this.gameRunning = false;
    this.onboardingPointer.disable();

    this.parkZones.forEach((z) => z.destroy());
    this.binZone.destroy();

    const goodItem = this.parkItems.find((i) => i.quality === 'Good') ? true : false;
    const neutralItem = this.parkItems.find((i) => i.quality === 'Neutral') ? true : false;
    const badItem = this.parkItems.find((i) => i.quality === 'Bad') ? true : false;

    let stars = 0;

    if (goodItem == true && neutralItem == false && badItem == false) {
      stars = 3;
    } else if (goodItem == true && neutralItem == true && badItem == false) {
      stars = 2;
    } else if (goodItem == false && neutralItem == false && badItem == true) {
      stars = 0;
    } else if (badItem == true) {
      stars = 1;
    }
    const win = stars >= 2;

    this.tweens.add({
      targets: [...this.parkItems],
      scale: 0,
      duration: 500,
    });

    this.game.soundController.stopMusic('music');
    const anim = win ? 'win' : 'lose';
    this.spineBin.play('out');
    this.spineBox.play('out');
    this.spineBG.play(anim, false);
    this.game.soundController.playSFX('general', win ? 'general_success' : 'general_fail', {
      delay: 1.3,
    });
    this.game.soundController.playSFX('beekind', win ? 'bee_success' : 'bee_fail', { delay: 2.2 });

    this.spineBG.once('complete', () => this.showEndGameScreen(win, stars));
  }

  shutdown() {
    this.game.soundController.stopAllSFX();
    super.shutdown();
    this.game.unloadGame(KEYS.BeeKind);
  }
}
